/**
 * NotificationBell Component
 *
 * Shows notification count badge and dropdown list.
 * Connects to WebSocket for real-time updates.
 */

import { useState, useEffect, useRef } from "react";
import { apiClient } from "../api/apiClient";

export default function NotificationBell({ userEmail, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications on mount and when dropdown opens
  useEffect(() => {
    if (userEmail) {
      fetchUnreadCount();
    }
  }, [userEmail]);

  useEffect(() => {
    if (showDropdown && userEmail) {
      fetchNotifications();
    }
  }, [showDropdown, userEmail]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Listen for WebSocket notifications
  useEffect(() => {
    function handleWebSocketMessage(event) {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "notification") {
          // New notification received
          setNotifications((prev) => [data.data, ...prev]);
          setUnreadCount((prev) => prev + 1);

          // Play notification sound
          playNotificationSound(data.data?.type);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Get WebSocket from window (set by WireframeApp)
    if (window.timeTrackerWs) {
      window.timeTrackerWs.addEventListener("message", handleWebSocketMessage);

      // Register this user for targeted notifications
      if (userEmail && window.timeTrackerWs.readyState === WebSocket.OPEN) {
        window.timeTrackerWs.send(JSON.stringify({
          type: "register",
          email: userEmail,
        }));
      }
    }

    return () => {
      if (window.timeTrackerWs) {
        window.timeTrackerWs.removeEventListener("message", handleWebSocketMessage);
      }
    };
  }, [userEmail]);

  const fetchUnreadCount = async () => {
    try {
      const response = await apiClient.get("/api/notifications/unread-count");
      setUnreadCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/api/notifications?limit=20");
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.patch(`/api/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.post("/api/notifications/mark-all-read");
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    // Navigate to action URL if provided
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
    setShowDropdown(false);
  };

  const playNotificationSound = (type) => {
    // Play different sounds based on notification type
    if (type === "reprint_needed") {
      // More urgent sound for reprints
      const audio = new Audio("/sounds/alert.mp3");
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } else {
      // Standard notification sound
      const audio = new Audio("/sounds/notification.mp3");
      audio.volume = 0.3;
      audio.play().catch(() => {});
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "proof_ready":
        return "📄";
      case "proof_approved":
        return "✅";
      case "reprint_needed":
        return "🔄";
      case "question":
        return "❓";
      default:
        return "🔔";
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-500 hover:text-gray-900 transition-colors"
        title="Notifications"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-slate-100 border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-indigo-400 hover:text-indigo-300"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-200 hover:bg-slate-100/50 transition-colors ${
                    !notification.read_at ? "bg-gray-700/30" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-medium truncate ${
                            !notification.read_at
                              ? "text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          {notification.title}
                        </span>
                        {!notification.read_at && (
                          <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      {notification.body && (
                        <p className="text-sm text-gray-500 truncate mt-0.5">
                          {notification.body}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{getTimeAgo(notification.created_at)}</span>
                        {notification.sender_name && (
                          <>
                            <span>•</span>
                            <span>from {notification.sender_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  if (onNavigate) {
                    onNavigate('my-notifications');
                  }
                }}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
